// Single source of truth for the hero node-graph's geometry and which nodes get
// special treatment. Imported by both NodeGraphBackground (which renders the
// SVG) and scripts/generate-hdr-nodes.mjs (which bakes the matching HDR
// overlay). They must agree exactly: the HDR disc is composited on top of the
// vector one, so any drift in geometry or node choice shows as a bright disc
// floating away from its dot.

import type { BuildGraphOptions } from './nodeGraph';

export const HERO_GRAPH: BuildGraphOptions = {
  width: 1600,
  height: 900,
  cols: 20,
  rows: 12,
  radius: 1.2,
  radiusJitter: 1.3,
};

/** Nodes drawn as brighter, glowing "live" nodes so the network reads as active. */
export const LIT_NODES = [54, 76, 93, 117, 134, 156, 173, 197];

/**
 * Each hero shape gets one node promoted to the HDR tier, chosen for where that
 * hero actually crops. The homepage is tall and shows the graph near-uncropped,
 * so its node sits upper-right at ~(75%, 20%), well clear of the left-aligned
 * copy and its scrim. Page heroes are only 500px tall, so `slice` crops away
 * roughly the top and bottom quarter — their node has to sit near the vertical
 * centre to survive, hence the far-right one at ~(90%, 50%), which is also the
 * largest of the lit nodes.
 */
export const BRIGHT_NODE = {
  hero: 76,
  page: 117,
} as const;

export type HeroVariant = keyof typeof BRIGHT_NODE;

/** Added to a node's radius for the bright disc. Must match the HDR overlay. */
export const BRIGHT_CORE_PAD = 0.4;

/** The lit nodes for a variant, excluding whichever one it promotes to HDR. */
export const litNodesFor = (variant: HeroVariant) =>
  LIT_NODES.filter(i => i !== BRIGHT_NODE[variant]);
